import {
  isCalendarDate,
  type CreateSuspensionRequest,
  type RevokeSuspensionRequest,
  type ServeSuspensionRequest,
  type SuspensionStatus,
  type SuspensionView,
  type UpdateSuspensionRequest,
} from '@stm/contracts';
import type { TranslationKey } from '@stm/i18n';

export const suspensionStatusKeys: Readonly<Record<SuspensionStatus, TranslationKey>> = {
  active: 'suspension.status.active',
  served: 'suspension.status.served',
  revoked: 'suspension.status.revoked',
};

export type SuspensionMeasure = 'days' | 'games';

/** The most games a suspension can count; the same limit the server and the database apply. */
export const maximumSuspensionGames = 99;

export interface SuspensionForm {
  athleteId: string;
  /** Empty: the suspension applies in every competition of the club. */
  competitionSeasonId: string;
  measure: SuspensionMeasure;
  startsOn: string;
  endsOn: string;
  remainingGames: string;
  decisionReference: string;
}

export const emptySuspensionForm: SuspensionForm = {
  athleteId: '',
  competitionSeasonId: '',
  measure: 'games',
  startsOn: '',
  endsOn: '',
  remainingGames: '',
  decisionReference: '',
};

function textOrNull(text: string): string | null {
  const trimmed = text.trim();
  return trimmed === '' ? null : trimmed;
}

/** A whole number of games from 0 to 99, or null while the text is anything else. */
export function parseGames(text: string): number | null {
  if (!/^\d{1,2}$/.test(text.trim())) return null;
  return Number(text.trim());
}

/** The create request, or null while the form is incomplete or its measure contradicts itself. */
export function suspensionRequest(form: SuspensionForm): CreateSuspensionRequest | null {
  if (form.athleteId === '' || !isCalendarDate(form.startsOn)) return null;
  const common = {
    athleteId: form.athleteId,
    competitionSeasonId: form.competitionSeasonId === '' ? null : form.competitionSeasonId,
    startsOn: form.startsOn,
    decisionReference: textOrNull(form.decisionReference),
  };
  if (form.measure === 'days') {
    if (!isCalendarDate(form.endsOn) || form.endsOn < form.startsOn) return null;
    return { ...common, endsOn: form.endsOn, remainingGames: null };
  }
  const games = parseGames(form.remainingGames);
  return games === null ? null : { ...common, endsOn: null, remainingGames: games };
}

export interface SuspensionCorrectionForm {
  startsOn: string;
  endsOn: string;
  remainingGames: string;
  decisionReference: string;
}

export function suspensionCorrectionFormOf(suspension: SuspensionView): SuspensionCorrectionForm {
  return {
    startsOn: suspension.startsOn,
    endsOn: suspension.endsOn ?? '',
    remainingGames: suspension.remainingGames === null ? '' : String(suspension.remainingGames),
    decisionReference: suspension.decisionReference ?? '',
  };
}

/**
 * Only the fields that differ from the record are sent, and only the measure the suspension has.
 * Null when nothing changed or a value is invalid, so the save button stays off.
 */
export function suspensionCorrectionRequest(
  form: SuspensionCorrectionForm,
  suspension: SuspensionView,
): UpdateSuspensionRequest | null {
  if (!isCalendarDate(form.startsOn)) return null;
  const request: UpdateSuspensionRequest = {};
  if (form.startsOn !== suspension.startsOn) request.startsOn = form.startsOn;
  if (suspension.endsOn !== null) {
    if (!isCalendarDate(form.endsOn) || form.endsOn < form.startsOn) return null;
    if (form.endsOn !== suspension.endsOn) request.endsOn = form.endsOn;
  } else {
    const games = parseGames(form.remainingGames);
    if (games === null) return null;
    if (games !== suspension.remainingGames) request.remainingGames = games;
  }
  const reference = textOrNull(form.decisionReference);
  if (reference !== suspension.decisionReference) request.decisionReference = reference;
  return Object.keys(request).length === 0 ? null : request;
}

export function serveRequest(note: string): ServeSuspensionRequest {
  return { note: textOrNull(note) };
}

/** The revocation request, or null while no reason is written. */
export function revokeRequest(reason: string): RevokeSuspensionRequest | null {
  const trimmed = reason.trim();
  return trimmed === '' ? null : { reason: trimmed };
}

const statusOrder: Readonly<Record<SuspensionStatus, number>> = {
  active: 0,
  served: 1,
  revoked: 2,
};

/** Active suspensions first, then served, then revoked; the server's order within each group. */
export function activeFirst(suspensions: readonly SuspensionView[]): SuspensionView[] {
  return suspensions
    .map((suspension, position) => ({ suspension, position }))
    .sort(
      (left, right) =>
        statusOrder[left.suspension.status] - statusOrder[right.suspension.status] ||
        left.position - right.position,
    )
    .map(({ suspension }) => suspension);
}

export function suspensionPath(
  base: string,
  suspensionId?: string,
  action?: 'serve' | 'revoke',
): string {
  const root = `${base}/suspensions`;
  if (suspensionId === undefined) return root;
  const one = `${root}/${encodeURIComponent(suspensionId)}`;
  return action === undefined ? one : `${one}/${action}`;
}
